import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useField } from 'react-final-form';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';

import { Row } from '../../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { ISignersForm } from '../../../KnmErknmPackage/MainModule/interfaces/ISignersForm';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import {
  canMatchOfficial,
  getSignerFio,
  getSignerIdentity,
  IOfficialIdentity,
  isSameOfficial,
} from '../common/actOfficialsSyncCommon';
import { syncActKnoInspectorsFromSigner } from '../common/syncActKnoInspectorsFromSigner';
import styles from './Signers.module.scss';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { IDecisionFormRef } from '../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IKnmErknmFormInspector } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { ISelectOption, isCreatableNewOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { IKnmErknmForm } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmPmForm } from '../../../KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { getRootSignersMode } from '../common/getRootSignersMode';
import { getNestedSignersMode } from '../common/getNestedSignersMode';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { RootSignersModeEnum } from '../enums/RootSignersModeEnum';
import { NestedSignersModeEnum } from '../enums/NestedSignersModeEnum';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmStatusCode } from '../../MainModule/selectors/knmSelectors';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';
import { IKnmStatus } from '../../../APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { renderDateForDisplayedSignatureInfo } from '../../../functions/renderDateForDisplayedSignatureInfo';
import { Textarea } from '../../../UIPackage/MainModule/components/Textarea';

interface ISignersProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  hasActOrResultDecisionFirstExtractDateTime?: boolean;
  hintText: string | undefined;
  inspectors: IKnmErknmFormInspector[] | undefined;
  isActOrResultDecisionSigned?: boolean;
  isDecision?: boolean;
  isKnm?: boolean;
  isNewActOrResultDecision?: boolean;
  isPm?: boolean;
  label: string | undefined;
  parentId: string;
  parentName: string;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
}

interface ISignersStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  statusCode: IKnmStatus['code'] | undefined;
}

const SignersWithoutStore = ({
  formRef,
  hasActOrResultDecisionFirstExtractDateTime,
  hintText,
  inspectors,
  isActOrResultDecisionSigned,
  isDecision,
  isKnm,
  isNewActOrResultDecision,
  isPm,
  label,
  parentName,
  parentId,
  positionsSelectOptions,
  isProsecutorOrSuperProsecutor,
  statusCode,
}: ISignersProps & ISignersStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const blockId = parentId?.length > 0 ? `${parentId}.signers` : 'signers';
  const blockName = parentName?.length > 0 ? `${parentName}.signers` : 'signers';
  // Синхронизация Signers → knoInspectors акта: Decision / КНМ / ПМ (не предписания и не root-signers).
  const shouldSyncActKnoInspectorsFromSigners =
    (isDecision === true && parentName === 'organization.act') ||
    (isKnm === true && parentName.startsWith('acts')) ||
    (isPm === true && parentName === 'act');

  const syncKnoInspectorsFromSigner = useCallback(
    ({
      mode,
      original,
      updated,
    }: {
      mode: 'update' | 'remove';
      original: ReturnType<typeof getSignerIdentity>;
      updated?: ReturnType<typeof getSignerIdentity>;
    }) => {
      if (!shouldSyncActKnoInspectorsFromSigners) {
        return;
      }

      syncActKnoInspectorsFromSigner({
        formRef,
        passportInspectors: inspectors,
        actFieldPrefix: parentName,
        mode,
        original,
        updated,
      });
    },
    [formRef, inspectors, parentName, shouldSyncActKnoInspectorsFromSigners],
  );

  const isRootSigners = !parentName.length && (isPm || isKnm);
  const hasActs = () => {
    if (isKnm) {
      return !!(formRef as IKnmFormRef | undefined)?.current.getFieldState('acts' as keyof IKnmErknmForm)?.value;
    }
    if (isPm) {
      return !!(formRef as IKnmFormRef | undefined)?.current.getFieldState('act' as keyof IKnmPmForm)?.value;
    }

    return false;
  };

  const rootMode = useMemo(() => {
    if (!isRootSigners) {
      return undefined;
    }

    return getRootSignersMode({
      hasActs: hasActs(),
      isApproving: statusCode === KnmErknmStatusCodesEnum.APPROVING,
      isCarriedOut: statusCode === KnmErknmStatusCodesEnum.TYPE_CARRIED_OUT,
      isCommentsExist: statusCode === KnmErknmStatusCodesEnum.COMMENTS_EXIST,
      isFillInProcess: statusCode === KnmErknmStatusCodesEnum.FILL_IN_PROCESS,
      isPm: isPm === true,
      isReadyForApprove: statusCode === KnmErknmStatusCodesEnum.READY_FOR_APPROVE,
      isWaitingCarryOut: statusCode === KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT,
    });
  }, [isRootSigners, hasActs, statusCode, isPm]);

  const nestedMode = useMemo(() => {
    if (isRootSigners || !parentName.length) {
      return undefined;
    }

    return getNestedSignersMode({
      hasActOrResultDecisionFirstExtractDateTime,
      isNewActOrResultDecision,
    });
  }, [hasActOrResultDecisionFirstExtractDateTime, isNewActOrResultDecision, isRootSigners, parentName]);

  const signersValue = useField(`${blockName}`, { subscription: { value: true } }).input.value as
    | ISignersForm[]
    | undefined;

  const isAddButtonVisible = isRootSigners
    ? rootMode === RootSignersModeEnum.MULTI_FIRST_LOCKED || rootMode === RootSignersModeEnum.READONLY_ALL
    : nestedMode === NestedSignersModeEnum.NEW_SINGLE || nestedMode === NestedSignersModeEnum.MULTI_FIRST_LOCKED;

  const isAddButtonDisabled = isRootSigners
    ? rootMode === RootSignersModeEnum.READONLY_ALL || isProsecutorOrSuperProsecutor
    : (nestedMode === NestedSignersModeEnum.NEW_SINGLE && signersValue && signersValue.length >= 1) ||
      isProsecutorOrSuperProsecutor;

  const isFirstSignerLocked = isRootSigners
    ? rootMode === RootSignersModeEnum.MULTI_FIRST_LOCKED || rootMode === RootSignersModeEnum.READONLY_ALL
    : nestedMode === NestedSignersModeEnum.MULTI_FIRST_LOCKED;

  const areAllSignersReadonly = isRootSigners && rootMode === RootSignersModeEnum.READONLY_ALL;

  /**
   * Если signers пустой — добавляем единственную запись.
   * push берём из form.current внутри эффекта: на первом рендере mutators ещё могут быть недоступны.
   */
  useEffect(() => {
    if (!isRootSigners || rootMode !== RootSignersModeEnum.SINGLE_AUTO || isProsecutorOrSuperProsecutor) {
      return;
    }

    const form = formRef?.current;

    if (!form?.mutators?.push) {
      return;
    }

    // @ts-ignore
    const currentSigners = (form.getFieldState(blockName)?.value ?? form.getState().values?.[blockName]) as
      | ISignersForm[]
      | undefined;

    if (!currentSigners?.length) {
      form.mutators.push(blockName, { isNew: true, listId: uuidv4() });
    }
  }, [blockName, formRef, isRootSigners, isProsecutorOrSuperProsecutor, rootMode, signersValue]);

  const addSigner = useCallback(() => {
    if (isAddButtonDisabled || !push) {
      return;
    }

    push(blockName, { isNew: true, listId: uuidv4() });
  }, [blockName, isAddButtonDisabled, push]);

  const hasAnotherSignerWithSameIdentity = useCallback(
    (identity: IOfficialIdentity, excludeListId: string) => {
      if (!identity.fullName || !identity.position) {
        return false;
      }

      return Boolean(
        signersValue?.some(signer => {
          if (signer.listId === excludeListId) {
            return false;
          }

          const otherIdentity = getSignerIdentity(signer);

          return isSameOfficial(otherIdentity.fullName, otherIdentity.position, identity);
        }),
      );
    },
    [signersValue],
  );

  const syncKnoInspectorsFromSignerIfNoDuplicate = useCallback(
    ({
      mode,
      original,
      updated,
      excludeListId,
    }: {
      mode: 'update' | 'remove';
      original: IOfficialIdentity;
      updated?: IOfficialIdentity;
      excludeListId: string;
    }) => {
      // Очистка/удаление: не трогаем knoInspectors, если такой подписант ещё есть в другой строке.
      if (mode === 'remove' || !updated || !canMatchOfficial(updated)) {
        if (hasAnotherSignerWithSameIdentity(original, excludeListId)) {
          return;
        }

        syncKnoInspectorsFromSigner({ mode, original });

        return;
      }

      const hasOtherUpdated = hasAnotherSignerWithSameIdentity(updated, excludeListId);
      const isIdentityChanged =
        canMatchOfficial(original) && !isSameOfficial(updated.fullName, updated.position, original);
      const hasOtherOriginal = isIdentityChanged && hasAnotherSignerWithSameIdentity(original, excludeListId);

      // Целевая пара уже обеспечена другой строкой Signers — только чистим original при необходимости.
      if (hasOtherUpdated) {
        if (isIdentityChanged && !hasOtherOriginal) {
          syncKnoInspectorsFromSigner({ mode: 'remove', original });
        }

        return;
      }

      // Original ещё нужен другой строке — не конвертируем его, только добавляем updated.
      if (hasOtherOriginal) {
        syncKnoInspectorsFromSigner({
          mode: 'update',
          original: { fullName: undefined, position: undefined },
          updated,
        });

        return;
      }

      syncKnoInspectorsFromSigner({ mode: 'update', original, updated });
    },
    [hasAnotherSignerWithSameIdentity, syncKnoInspectorsFromSigner],
  );

  const makeRemoveKnmResultHandler = useCallback(
    (index: number, fieldValue: ISignersForm) => () => {
      syncKnoInspectorsFromSignerIfNoDuplicate({
        mode: 'remove',
        original: getSignerIdentity(fieldValue),
        excludeListId: fieldValue.listId,
      });
      remove && remove(blockName, index);
    },
    [blockName, remove, syncKnoInspectorsFromSignerIfNoDuplicate],
  );

  /**
   * Список вариантов для выбора должностного лица
   */
  const inspectorsOptions = useMemo(() => {
    return (
      inspectors
        ?.filter(inspector => inspector.fullName)
        .map(inspector => {
          return {
            id: inspector.guid || '',
            value: inspector.fullName,
            label: `${inspector.fullName}`,
          };
        }) || []
    );
  }, [inspectors]);

  /** Уже выбранные в других строках подписанты не предлагаем повторно. */
  const getInspectorsOptions = useCallback(
    (currentSigner: ISignersForm) => {
      const selectedFios = new Set(
        (signersValue || [])
          .filter(signer => signer.listId !== currentSigner.listId)
          .map(signer => getSignerFio(signer.fioSigner)?.trim())
          .filter((fio): fio is string => Boolean(fio)),
      );

      return inspectorsOptions.filter(option => !selectedFios.has(String(option.value || '').trim()));
    },
    [inspectorsOptions, signersValue],
  );

  const normalizeSignerFio = useCallback((value: string | undefined, trim = true) => {
    if (value == null) {
      return value;
    }

    let normalized = value
      .replace(/[^A-Za-zА-Яа-яЁё\-‑'’.,() ]/g, '')
      .replace(/[-‑'’.,()]{2,}/g, match => match[0])
      .replace(/ {2,}/g, ' ')
      .replace(/^[^A-Za-zА-Яа-яЁё]+/, '');

    if (trim) {
      normalized = normalized.trim();
    }

    normalized = normalized
      .toLocaleLowerCase('ru-RU')
      .replace(/[A-Za-zА-Яа-яЁё]+/g, word => `${word.charAt(0).toLocaleUpperCase('ru-RU')}${word.slice(1)}`);

    return normalized;
  }, []);

  const handleSignerFioInputChange = useCallback((inputValue: string) => normalizeSignerFio(inputValue, false) || '', [
    normalizeSignerFio,
  ]);

  const handleFioAndPositionSingerChange = useCallback(
    (value: ISelectOption | undefined, name: string, fieldValue: ISignersForm) => {
      const original = getSignerIdentity(fieldValue);
      const isNewOption = isCreatableNewOption(value);
      const enteredFio = normalizeSignerFio(String(value?.value || value?.label || ''));
      const inspector =
        inspectors && !isNewOption
          ? inspectors.find(item => normalizeSignerFio(item.fullName || '') === enteredFio)
          : undefined;

      if (inspector) {
        // Явно пишем оба поля в форму, чтобы повторная валидация шла по итоговому состоянию пары fio/title.
        // @ts-ignore
        formRef?.current?.change(`${name}.fioSigner`, {
          id: value?.id || inspector.guid || '',
          label: normalizeSignerFio(inspector.fullName) || '',
          value: normalizeSignerFio(inspector.fullName) || '',
        });
        // @ts-ignore
        formRef?.current?.change(`${name}.titleSigner`, inspector.position);
        syncKnoInspectorsFromSignerIfNoDuplicate({
          mode: 'update',
          original,
          updated: {
            fullName: inspector.fullName,
            position: inspector.position,
          },
          excludeListId: fieldValue.listId,
        });

        return;
      }

      if (value) {
        const manualFio = normalizeSignerFio(String(value.value || value.label || '')) || '';
        const manualLabel = normalizeSignerFio(value.label) || manualFio;

        // @ts-ignore
        formRef?.current?.change(`${name}.fioSigner`, {
          id: value.id || '',
          label: manualLabel,
          value: manualFio,
          ...(isNewOption ? { __isNew__: true } : {}),
        });
        // @ts-ignore
        formRef?.current?.change(`${name}.titleSigner`);
        syncKnoInspectorsFromSignerIfNoDuplicate({
          mode: 'update',
          original,
          excludeListId: fieldValue.listId,
        });

        return;
      }

      syncKnoInspectorsFromSignerIfNoDuplicate({
        mode: 'update',
        original,
        excludeListId: fieldValue.listId,
      });

      // @ts-ignore
      formRef?.current?.change(`${name}.fioSigner`);
      // @ts-ignore
      formRef?.current?.change(`${name}.titleSigner`);
    },
    [formRef, inspectors, normalizeSignerFio, syncKnoInspectorsFromSignerIfNoDuplicate],
  );

  const handleTitleSignerChange = useCallback(
    (position: IDictionaryParticipantPositionSelectOption | undefined, fieldValue: ISignersForm) => {
      const original = getSignerIdentity(fieldValue);

      syncKnoInspectorsFromSignerIfNoDuplicate({
        mode: 'update',
        original,
        updated: {
          fullName: original.fullName,
          position,
        },
        excludeListId: fieldValue.listId,
      });
    },
    [syncKnoInspectorsFromSignerIfNoDuplicate],
  );

  return (
    <FieldBlock
      label={getTitleFieldWithRedStar({
        label: label || '',
        field: blockName,
        isDecision,
        isKnm,
        isPm,
      })}
      id={blockId}
      button={
        isAddButtonVisible ? (
          <Button
            className="shared-btn-add"
            text="Добавить"
            variant="link"
            color="primary"
            type="button"
            disabled={isAddButtonDisabled}
            onClick={addSigner}
          />
        ) : undefined
      }
      margin="bottom"
      className={styles.SignersBlock}
    >
      <FieldArray name={blockName}>
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: ISignersForm = fields.value?.[index];
            const id = `${blockId}[${fieldValue.listId}]`;
            const isRowDisabled =
              areAllSignersReadonly ||
              (isFirstSignerLocked && index === 0) ||
              fieldValue.existedAtSigning === true ||
              isProsecutorOrSuperProsecutor;

            const canRemove = () => {
              if (isRootSigners) {
                return fieldValue.isNew && !isRowDisabled && (fields.length ?? 0) > 1;
              }

              if (nestedMode === NestedSignersModeEnum.ADD_HIDDEN) {
                return false;
              }

              return fieldValue.isNew && !isRowDisabled;
            };

            if (fieldValue.systemSignature?.systemSignatureTitle) {
              const signerTitle = `Паспорт подписан по СМЭВ ${
                fieldValue.signatureDateTime ? renderDateForDisplayedSignatureInfo(fieldValue.signatureDateTime) : ''
              } в автоматическом режиме подписью системы "${fieldValue.systemSignature.systemSignatureTitle}"`;

              return (
                <Row isListItem notWrap className={styles.SystemSignatureRow} margin="none" key={id} id={id}>
                  <FieldBlock color="black" margin="right" className={styles.SignerFioField}>
                    <Textarea value={signerTitle} disabled={true} resize="none" />
                  </FieldBlock>
                  <Row notWrap>
                    <FieldBlock color="black" className={styles.SignerPositionField}>
                      <Textarea
                        value={'-'}
                        disabled={true}
                        resize="none"
                        autoHeight={false}
                        className={styles.SystemSignatureTextarea}
                        inputClassName={styles.SystemSignatureTextareaInput}
                      />
                    </FieldBlock>
                  </Row>
                </Row>
              );
            }

            const fioSignerOption =
              typeof fieldValue.fioSigner === 'object' ? (fieldValue.fioSigner as ISelectOption) : undefined;
            const isFioFromInspectors =
              (!!fioSignerOption &&
                !isCreatableNewOption(fioSignerOption) &&
                !!inspectors?.some(inspector => inspector.fullName === fioSignerOption.value)) ||
              (!fieldValue.fioSigner && !!inspectors);

            return (
              <Row id={id} isListItem key={id} notWrap margin="none">
                <FieldBlock color="black" margin="right" className={styles.SignerFioField}>
                  {inspectors ? (
                    <SelectInputField
                      id={`${id}.fioSigner`}
                      name={`${name}.fioSigner`}
                      options={getInspectorsOptions(fieldValue)}
                      disabled={isRowDisabled}
                      placeholder={getTitleFieldWithRedStar({
                        label: 'ФИО подписанта',
                        field: `${name}.fioSigner`,
                        isDecision,
                        isKnm,
                        isPm,
                      })}
                      size="medium"
                      isCreatable={hasActOrResultDecisionFirstExtractDateTime}
                      allowCreateOnExactMatch={hasActOrResultDecisionFirstExtractDateTime}
                      isSearchable
                      onInputChange={handleSignerFioInputChange}
                      onChange={(val: ISelectOption | undefined) =>
                        handleFioAndPositionSingerChange(val, name, fieldValue)
                      }
                    />
                  ) : (
                    <TextInputField
                      id={`${id}.fioSigner`}
                      name={`${name}.fioSigner`}
                      parse={(value: string | undefined) => normalizeSignerFio(value, false)}
                      disabled={isRowDisabled}
                      placeholder={getTitleFieldWithRedStar({
                        label: 'ФИО подписанта',
                        field: `${name}.fioSigner`,
                        isDecision,
                        isKnm,
                        isPm,
                      })}
                    />
                  )}
                </FieldBlock>
                <Row notWrap>
                  <FieldBlock color="black" className={styles.SignerPositionField}>
                    <SelectInputField
                      id={`${id}.titleSigner`}
                      name={`${name}.titleSigner`}
                      options={positionsSelectOptions}
                      disabled={isRowDisabled || isFioFromInspectors}
                      placeholder={getTitleFieldWithRedStar({
                        label: 'Должность подписанта',
                        field: `${name}.titleSigner`,
                        isDecision,
                        isKnm,
                        isPm,
                      })}
                      size="medium"
                      onChange={(position: IDictionaryParticipantPositionSelectOption | undefined) =>
                        handleTitleSignerChange(position, fieldValue)
                      }
                    />
                  </FieldBlock>
                  {canRemove() && (
                    <CloseButton
                      className={'shared-deleting-row-btn'}
                      onClick={canRemove() ? makeRemoveKnmResultHandler(index, fieldValue) : undefined}
                      disabled={!canRemove()}
                    />
                  )}
                </Row>
              </Row>
            );
          });
        }}
      </FieldArray>
      {(!areAllSignersReadonly ||
        isActOrResultDecisionSigned === false ||
        (isAddButtonVisible && !isAddButtonDisabled)) &&
        hintText && <Hint text={hintText} />}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState, ownProps: ISignersProps): ISignersStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  statusCode: !ownProps.isDecision ? getKnmStatusCode(state) : undefined,
});

export const Signers = connect(mapStateToProps)(SignersWithoutStore);
